import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function ArticlePage() {
  const { t, i18n } = useTranslation('article');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const lang = i18n.language || 'en';
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const article = t('', { returnObjects: true }) as {
    title: string;
    metaTitle: string;
    metaDescription: string;
    publishedDate: string;
    updatedDate: string;
    readTime: number;
    author: string;
    introduction: string;
    sections: Array<{ id: string; h2: string; content: string }>;
    faq: Array<{ question: string; answer: string }>;
    relatedArticles: Array<{ title: string; slug: string; summary: string }>;
  };

  const handleFaqToggle = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Article Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20top-down%20view%20of%20a%20bright%20modern%20workspace%20with%20a%20laptop%20showing%20a%20product%20spreadsheet%20surrounded%20by%20folded%20clothing%20and%20a%20small%20green%20plant%2C%20soft%20natural%20daylight%2C%20clean%20editorial%20photography%20with%20a%20fresh%20minimal%20warm%20neutral%20aesthetic&width=1600&height=500&seq=article-hero&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet Guide"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/70" />
        </div>
        <div className="relative z-10 px-4 md:px-8 lg:px-16 xl:px-24 py-20 md:py-28">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-300 text-sm font-medium mb-6">
              <i className="ri-book-open-line" />
              <span>Guide</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              {article.title || 'CSSBuy Spreadsheet Guide'}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.publishedDate || '2026-01-15'}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {article.readTime || 12} {tc('article.minRead')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author || 'CSSBuy Spreadsheet Team'}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line" />
                {tc('article.updated')}: {article.updatedDate || '2026-08-27'}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 lg:px-16 xl:px-24 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Sidebar - TOC */}
            <aside className="lg:w-72 shrink-0">
              <div className="sticky top-24">
                <div className="bg-white rounded-xl border border-background-200 p-5 mb-6">
                  <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4 uppercase tracking-wider">
                    {tc('article.tableOfContents')}
                  </h3>
                  <nav className="space-y-2">
                    <a
                      href="#introduction"
                      className="block text-sm text-foreground-600 hover:text-primary-600 py-1.5 transition-colors"
                    >
                      Introduction
                    </a>
                    {(article.sections || []).map((section) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="block text-sm text-foreground-600 hover:text-primary-600 py-1.5 transition-colors"
                      >
                        {section.h2}
                      </a>
                    ))}
                    <a
                      href="#faq"
                      className="block text-sm text-foreground-600 hover:text-primary-600 py-1.5 transition-colors"
                    >
                      {tc('article.faq')}
                    </a>
                  </nav>
                </div>

                <div className="bg-primary-50 rounded-xl border border-primary-100 p-5">
                  <h3 className="font-heading text-sm font-semibold text-primary-800 mb-3">
                    Ready to Shop?
                  </h3>
                  <p className="text-sm text-primary-700 mb-4 leading-relaxed">
                    Visit our main store to browse the full CSSBuy Spreadsheet with verified products and sellers.
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="btn-primary w-full text-sm py-2.5"
                  >
                    <i className="ri-store-2-line mr-1.5" />
                    CSSBuy Spreadsheet
                  </a>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <article className="flex-1">
              <div className="bg-white rounded-xl border border-background-200 p-6 md:p-10">
                {/* Introduction */}
                <div id="introduction" className="mb-10">
                  <p className="text-foreground-700 leading-relaxed text-base">
                    {article.introduction}
                  </p>
                </div>

                {/* Sections */}
                {(article.sections || []).map((section, idx) => (
                  <div key={section.id} id={section.id} className="mb-10 scroll-mt-24">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                      {section.h2}
                    </h2>
                    <p className="text-foreground-700 leading-relaxed text-base">
                      {section.content}
                    </p>
                    {idx === 1 && (
                      <div className="mt-6 p-5 bg-background-50 rounded-xl border border-background-200">
                        <h3 className="font-heading text-lg font-semibold text-foreground-800 mb-3">
                          Key Benefits at a Glance
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            'Verified product links',
                            'Trusted seller ratings',
                            'Shipping weight estimates',
                            'QC photo references',
                            'Community feedback',
                            'Updated regularly',
                          ].map((item) => (
                            <div key={item} className="flex items-center gap-2 text-sm text-foreground-600">
                              <i className="ri-check-line text-primary-500" />
                              <span>{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {idx === 3 && (
                      <div className="mt-6 overflow-hidden rounded-xl border border-background-200">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-background-100">
                              <th className="text-left px-4 py-3 font-semibold text-foreground-800">Feature</th>
                              <th className="text-left px-4 py-3 font-semibold text-foreground-800">What to Check</th>
                              <th className="text-left px-4 py-3 font-semibold text-foreground-800">Why It Matters</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-background-200">
                            {[
                              ['Spreadsheet', 'Products, sellers, links', 'Find verified items faster'],
                              ['QC Photos', 'Quality, defects, sizing', 'Verify before shipping'],
                              ['Shipping', 'Weight, volume, method', 'Calculate true costs'],
                              ['Warehouse', 'Storage time, processing', 'Plan your timeline'],
                              ['Sellers', 'Reputation, feedback', 'Avoid scams and bad buys'],
                            ].map(([feature, check, why]) => (
                              <tr key={feature} className="bg-white">
                                <td className="px-4 py-3 font-medium text-foreground-700">{feature}</td>
                                <td className="px-4 py-3 text-foreground-600">{check}</td>
                                <td className="px-4 py-3 text-foreground-600">{why}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}

                {/* FAQ */}
                <div id="faq" className="mt-12 scroll-mt-24">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    {tc('article.faq')}
                  </h2>
                  <div className="space-y-3">
                    {(article.faq || []).map((faq, index) => (
                      <div
                        key={index}
                        className="border border-background-200 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => handleFaqToggle(index)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-background-50 transition-colors"
                        >
                          <span className="font-semibold text-foreground-800 text-sm pr-4">
                            {faq.question}
                          </span>
                          <i
                            className={`ri-arrow-down-s-line text-foreground-400 transition-transform shrink-0 ${
                              openFaq === index ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {openFaq === index && (
                          <div className="px-5 pb-5">
                            <p className="text-sm text-foreground-600 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Box */}
                <div className="mt-12 p-6 md:p-8 bg-primary-50 rounded-2xl border border-primary-100">
                  <h3 className="font-heading text-xl font-bold text-primary-800 mb-3">
                    Start Using CSSBuy Spreadsheet Today
                  </h3>
                  <p className="text-primary-700 text-sm leading-relaxed mb-6">
                    Ready to find verified products, trusted sellers, and complete shopping guides? Visit our main store for the full CSSBuy Spreadsheet experience.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="btn-primary text-sm"
                    >
                      <i className="ri-store-2-line mr-1.5" />
                      CSSBuy Spreadsheet
                    </a>
                    <a
                      href={`/${lang}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/${lang}`);
                      }}
                      className="btn-outline text-sm"
                    >
                      <i className="ri-home-4-line mr-1.5" />
                      {tc('buttons.backToHome')}
                    </a>
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              <div className="mt-10">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  {tc('article.relatedArticles')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(article.relatedArticles || []).map((related, idx) => (
                    <button
                      key={idx}
                      onClick={() => navigate(related.slug)}
                      className="group text-left p-5 rounded-xl bg-white border border-background-200 hover:border-primary-300 transition-all duration-200"
                    >
                      <h4 className="font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors text-sm mb-2">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 leading-relaxed">
                        {related.summary}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Back to Home */}
              <div className="mt-10 text-center">
                <a
                  href={`/${lang}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/${lang}`);
                  }}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  {tc('buttons.backToHome')}
                </a>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}